import React, {
  ChangeEvent,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import {
  SALESBOT2290_LOGGING_OPTIONS,
  SALESBOT2290_MONTH_OPTIONS,
  SALESBOT2290_UI_TEXT,
} from '../../constants/constants';
import { zenworksServiceApi } from '../../service/zenworksService.api';
import { isValidEmail } from '../../utils/validations';
import ButtonWithLoading from '../common/ButtomWIthLoading';
import socket from '../../socket';

interface CalculateTaxAmountProps {}

type WeightOption = { name: string; value: string };

interface FormFields {
  email: string;
  month: string;
  logging: string;
  weight: string;
}

const INITIAL_FORM: FormFields = {
  email: '',
  month: '',
  logging: '',
  weight: '',
};

const selectClass =
  'block appearance-none w-full bg-white border-2 border-gray-300 hover:border-secondary px-4 py-2 pr-8 rounded-lg shadow leading-tight focus:outline-none focus:shadow-outline disabled:opacity-50 disabled:cursor-not-allowed';

function stripBracketLabelPrefix(label: string): string {
  return String(label).replace(/^\[[^\]]+\]\s*/, '').trim();
}

function normalizeWeightOptions(raw: unknown): WeightOption[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item: any) => {
      const key =
        item?.key !== undefined && item?.key !== null
          ? String(item.key)
          : '';
      const rawLabel =
        item?.value !== undefined && item?.value !== null
          ? String(item.value)
          : '';
      if (!key) return null;
      const display = stripBracketLabelPrefix(rawLabel);
      return {
        name: display || rawLabel,
        value: key,
      };
    })
    .filter((item): item is WeightOption => item !== null);
}

// function formatEstimate(value: unknown): string {
//   if (value === null || value === undefined) return '';
//   if (typeof value === 'number' && !Number.isNaN(value)) {
//     return value.toLocaleString(undefined, {
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2,
//     });
//   }
//   return String(value);
// }

function weightKeyForPayload(key: string): number | string {
  const t = key.trim();
  if (!t) return '';
  if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);
  return t;
}

function monthNumberForPayload(month: string): number {
  const n = Number(month.trim());
  return Number.isFinite(n) ? n : NaN;
}

const CalculateTaxAmount: React.FC<CalculateTaxAmountProps> = () => {
  const connectionStatus = useSelector(
    (state: RootState) => state.socketConnection.status,
  );

  const [form, setForm] = useState<FormFields>(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [estimatedTax, setEstimatedTax] = useState('');
  const [weightOptions, setWeightOptions] = useState<WeightOption[]>([]);
  const [weightsLoading, setWeightsLoading] = useState(false);

  const disabled = connectionStatus !== 'Connected';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setWeightsLoading(true);
      const res = await zenworksServiceApi.getWeight();
      if (cancelled) return;
      setWeightsLoading(false);
      const content = res?.content;
      const list = normalizeWeightOptions(content);
      setWeightOptions(list);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateField = useCallback((name: keyof FormFields, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const isFormValid = useMemo(() => {
    return (
      isValidEmail(form.email) &&
      form.month !== '' &&
      form.logging !== '' &&
      form.weight.trim() !== ''
    );
  }, [form]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isFormValid || disabled) return;

    setLoading(true);
    setError('');
    try {
      const monthNum = monthNumberForPayload(form.month);
      const res = await zenworksServiceApi.calculateTax2290({
        email: form.email.trim(),
        month: monthNum,
        logging: form.logging,
        weight: weightKeyForPayload(form.weight),
      });

      if (res.success && res.content && res.content.result !== undefined) {
        setEstimatedTax(res.content.result);
      } else {
        setEstimatedTax('');
        setError(res.message || 'Unable to calculate tax.');
      }
      const roomId = localStorage.getItem('userNumber');
      const followup_prompt = `Generate a short chatbot message after a user completes a 2290 tax calculation.
                                Structure:
                                Acknowledge the tax estimate is ready
                                Highlight some 2290.ai benefits (AI-powered filing, 82% time savings, transparent pricing, 24/7 support)
                                End with subtle urgency to file early to avoid penalties, delays, or missed deadlines

                                Tone requirements:
                                Conversational, trustworthy, and helpful
                                Create urgency without sounding pushy or overly salesy
                                Keep the response concise within 2-3 lines only
                                `;           
      socket.emit('followup_prompt_trigger', followup_prompt, roomId);
    } catch (err: any) {
      setEstimatedTax('');
      setError(err?.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-2">
      <div className="text-[#383E49] text-left">
        {SALESBOT2290_UI_TEXT.CARD_TITLE}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-3 gap-y-4">
          <div className="flex flex-col flex-1 min-w-[100px]">
            <label
              htmlFor="salesbot2290-month"
              className="block mb-1 text-sm font-semibold text-gray-600"
            >
              {SALESBOT2290_UI_TEXT.MONTH_LABEL}
            </label>
            <select
              id="salesbot2290-month"
              name="month"
              className={selectClass}
              value={form.month}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                updateField('month', e.target.value)
              }
              disabled={disabled}
            >
              <option value="">
                {SALESBOT2290_UI_TEXT.SELECT_PLACEHOLDER}
              </option>
              {SALESBOT2290_MONTH_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col flex-1 min-w-[100px]">
            <label
              htmlFor="tax2290-logging"
              className="block mb-1 text-sm font-semibold text-gray-600"
            >
              {SALESBOT2290_UI_TEXT.LOGGING_LABEL}
            </label>
            <select
              id="tax2290-logging"
              name="logging"
              className={selectClass}
              value={form.logging}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                updateField('logging', e.target.value)
              }
              disabled={disabled}
            >
              <option value="">
                {SALESBOT2290_UI_TEXT.SELECT_PLACEHOLDER}
              </option>
              {SALESBOT2290_LOGGING_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col flex-1 min-w-[140px]">
            <label
              htmlFor="tax2290-weight"
              className="block mb-1 text-sm font-semibold text-gray-600"
            >
              {SALESBOT2290_UI_TEXT.WEIGHT_LABEL}
            </label>
            <select
              id="tax2290-weight"
              name="weight"
              className={selectClass}
              value={form.weight}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                updateField('weight', e.target.value)
              }
              disabled={disabled || weightsLoading}
            >
              <option value="">
                {weightsLoading
                  ? 'Loading…'
                  : SALESBOT2290_UI_TEXT.SELECT_PLACEHOLDER}
              </option>
              {weightOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-16 sm:items-end sm:justify-between">
          <div className="flex flex-col w-full sm:flex-1 sm:max-w-[min(50%,300px)]">
            <label
              htmlFor="tax2290-email"
              className="block mb-1 text-sm font-semibold text-gray-600"
            >
              {SALESBOT2290_UI_TEXT.EMAIL_LABEL}
            </label>
            <input
              id="tax2290-email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder={SALESBOT2290_UI_TEXT.EMAIL_PLACEHOLDER}
              className={selectClass}
              value={form.email}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                updateField('email', e.target.value)
              }
              disabled={disabled}
            />
          </div>

          <div className="flex justify-end sm:pb-0 pb-1">
            <ButtonWithLoading
              isLoading={loading}
              type="submit"
              label={SALESBOT2290_UI_TEXT.SUBMIT_BUTTON_LABEL}
              className={`rounded-full bg-primary text-white px-8 py-2.5 font-medium shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed ${
                isFormValid && !disabled
                  ? 'hover:bg-secondary hover:text-gray-900 cursor-pointer'
                  : 'opacity-60 cursor-not-allowed'
              }`}
              disabled={!isFormValid || disabled || loading}
            />
          </div>
        </div>

        {error ? (
          <div className="text-sm text-red-600 text-left" role="alert">
            {error}
          </div>
        ) : null}

        {estimatedTax !== '' ? (
          <div className="text-left pt-1">
            <span className="text-gray-600 text-sm">Estimated tax: </span>
            <strong className="text-xl text-gray-900 border-b-2 border-gray-800">
              ${estimatedTax}
            </strong>
          </div>
        ) : null}
      </form>
    </div>
  );
};

export default CalculateTaxAmount;
